import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CountryCode } from 'libphonenumber-js';
import { Minimize2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { FileDropzone } from '@/components/shared/file-dropzone';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ProgressBar } from '@/components/ui/progress-bar';
import { Stepper } from '@/components/ui/stepper';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';
import { formatNumber } from '@/utils/format';

import {
  buildInitialMapping,
  IMPORT_FIELD_GROUP_LABEL_KEYS,
  IMPORT_FIELD_GROUPS,
  IMPORT_FIELD_LABEL_KEYS,
  IMPORT_FIELDS,
  MAX_IMPORT_ROWS,
  parseCsv,
  prepareImportRows,
  REQUIRED_IMPORT_FIELDS,
  SAMPLE_CSV,
  type ImportField,
  type ImportRowPayload,
  type PreparedImportRow,
  type RowIssue,
} from './member-import-csv';
import {
  progressFromImportEvent,
  subscribeMemberImportEvents,
  type MemberImportProgress,
} from './member-import-realtime';
import {
  MemberImportStatusIndicator,
  type MemberImportIndicatorStatus,
} from './member-import-status-indicator';
import { useMemberImportNavigationGuard } from './use-member-import-navigation-guard';

type Step = 0 | 1 | 2 | 3;

interface ImportResultRow {
  index: number;
  ok: boolean;
  memberId?: string;
  error?: string;
  params?: Record<string, unknown>;
}

interface MemberImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCountry: CountryCode;
  onImported?: (memberIds: string[]) => void;
  /** Where to go after a successful import. Defaults to duplicate review. */
  afterImportNavigation?: 'duplicates' | 'none';
}

const displayName = (row: PreparedImportRow, mapping: Record<string, ImportField | ''>) => {
  if (row.payload) return `${row.payload.firstName} ${row.payload.lastName}`.trim();
  const firstHeader = Object.entries(mapping).find(([, field]) => field === 'firstName')?.[0];
  const lastHeader = Object.entries(mapping).find(([, field]) => field === 'lastName')?.[0];
  const first = firstHeader ? (row.source[firstHeader] ?? '') : '';
  const last = lastHeader ? (row.source[lastHeader] ?? '') : '';
  const name = `${first} ${last}`.trim();
  return name || '—';
};

/** Spreadsheet row number — row 1 is the header, first member is row 2. */
const spreadsheetRowNumber = (index: number) => index + 2;

export const MemberImportDialog = ({
  open,
  onOpenChange,
  defaultCountry,
  onImported,
  afterImportNavigation = 'duplicates',
}: MemberImportDialogProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userId = useAuthStore((state) => state.user?.id);

  const [step, setStep] = useState<Step>(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, ImportField | ''>>({});
  const [appConnectEmailSameAsEmail, setAppConnectEmailSameAsEmail] = useState(true);
  const [appConnectPhoneSameAsPhone, setAppConnectPhoneSameAsPhone] = useState(true);
  const [results, setResults] = useState<ImportResultRow[] | null>(null);
  const [importRowCount, setImportRowCount] = useState(0);
  const [importProgress, setImportProgress] = useState<MemberImportProgress | null>(null);
  const [backgroundSession, setBackgroundSession] = useState(false);
  const openRef = useRef(open);
  const importUnsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const reset = () => {
    importUnsubscribeRef.current?.();
    importUnsubscribeRef.current = null;
    setStep(0);
    setFileName(null);
    setFileError(null);
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setAppConnectEmailSameAsEmail(true);
    setAppConnectPhoneSameAsPhone(true);
    setResults(null);
    setImportRowCount(0);
    setImportProgress(null);
    setBackgroundSession(false);
  };

  const appConnectOptions = useMemo(
    () => ({
      appConnectEmailSameAsEmail,
      appConnectPhoneSameAsPhone,
    }),
    [appConnectEmailSameAsEmail, appConnectPhoneSameAsPhone],
  );

  const prepared = useMemo(
    () =>
      prepareImportRows({
        rows: rawRows,
        mapping,
        defaultCountry,
        appConnect: appConnectOptions,
      }),
    [rawRows, mapping, defaultCountry, appConnectOptions],
  );

  const readyRows = prepared.filter((row): row is PreparedImportRow & { payload: ImportRowPayload } =>
    Boolean(row.payload),
  );
  const problemRows = prepared.filter((row) => row.issues.length > 0);

  const mappedRequired = REQUIRED_IMPORT_FIELDS.every((field) =>
    Object.values(mapping).includes(field),
  );

  const fieldOptions = [
    { value: '', label: t('import.fields.skip') },
    ...IMPORT_FIELDS.map((field) => ({
      value: field,
      label: t(IMPORT_FIELD_LABEL_KEYS[field]),
      group: t(IMPORT_FIELD_GROUP_LABEL_KEYS[IMPORT_FIELD_GROUPS[field]]),
    })),
  ];

  const issueLabel = (issue: RowIssue) => t(`import.issues.${issue}`);

  const importMutation = useMutation({
    mutationFn: (input: { rows: ImportRowPayload[]; importId: string }) =>
      api.post<{ results: ImportResultRow[] }>('/people/import', input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['people'] });
      const succeeded = data.results.filter((row) => row.ok).length;
      toast.success(t('import.toast.done', { count: succeeded }));
      const importedMemberIds = data.results.filter((row) => row.ok && row.memberId).map((row) => row.memberId!);
      if (importedMemberIds.length > 0) {
        onImported?.(importedMemberIds);
      }

      const completingInBackground = backgroundSession && !openRef.current;

      if (!completingInBackground && importedMemberIds.length > 0) {
        reset();
        importMutation.reset();
        onOpenChange(false);
        if (afterImportNavigation === 'duplicates') {
          void navigate(`/people/duplicates?run=1&memberIds=${importedMemberIds.join(',')}`);
        }
        return;
      }

      setResults(data.results);
      setStep(3);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const importNavigationGuard = useMemberImportNavigationGuard({
    active: importMutation.isPending,
  });

  const dismissSession = () => {
    reset();
    importMutation.reset();
  };

  const subscribeToImport = (importId: string) => {
    importUnsubscribeRef.current?.();
    if (!userId) return;
    importUnsubscribeRef.current = subscribeMemberImportEvents(userId, importId, (event) => {
      setImportProgress((previous) => progressFromImportEvent(event, previous));
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      if (importMutation.isPending) {
        setBackgroundSession(true);
        onOpenChange(false);
        return;
      }
      if (step === 3 && results) {
        dismissSession();
        onOpenChange(false);
        return;
      }
    }
    onOpenChange(nextOpen);
  };

  const indicatorStatus = useMemo((): MemberImportIndicatorStatus | null => {
    if (importMutation.isPending) return 'importing';
    if (importMutation.isError) return 'error';
    if (!results) return null;
    const succeeded = results.filter((row) => row.ok).length;
    const failed = results.filter((row) => !row.ok).length;
    if (succeeded > 0 && failed === 0) return 'success';
    if (succeeded > 0 && failed > 0) return 'partial';
    return 'failed';
  }, [importMutation.isPending, importMutation.isError, results]);

  const indicatorLabel = useMemo(() => {
    if (!indicatorStatus) return '';
    if (indicatorStatus === 'importing') {
      if (importProgress && importProgress.processed > 0) {
        return t('import.background.importingProgress', {
          processed: formatNumber(importProgress.processed),
          total: formatNumber(importProgress.total),
        });
      }
      return t('import.background.importing', { count: importRowCount });
    }
    if (indicatorStatus === 'error') {
      return t('import.background.error');
    }
    const succeeded = results?.filter((row) => row.ok).length ?? 0;
    const failed = results?.filter((row) => !row.ok).length ?? 0;
    if (indicatorStatus === 'success') {
      return t('import.background.success', { count: succeeded });
    }
    if (indicatorStatus === 'partial') {
      return t('import.background.partial', { succeeded, failed });
    }
    return t('import.background.failed', { failed });
  }, [indicatorStatus, importProgress, importRowCount, results, t]);

  const backgroundActive = backgroundSession;

  useEffect(() => {
    if (!open && !backgroundSession) {
      reset();
    }
  }, [open, backgroundSession]);

  const onFiles = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setFileError(null);

    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setFileError(t('import.upload.invalidType'));
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.headers.length === 0 || parsed.rows.length === 0) {
        setFileError(t('import.upload.empty'));
        return;
      }
      if (parsed.rows.length > MAX_IMPORT_ROWS) {
        setFileError(t('import.upload.tooMany', { max: formatNumber(MAX_IMPORT_ROWS) }));
        return;
      }
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRawRows(parsed.rows);
      setMapping(buildInitialMapping(parsed.headers));
      setStep(1);
    } catch {
      setFileError(t('import.upload.readFailed'));
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'people-import-sample.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const steps = [
    { id: 'upload', label: t('import.steps.upload') },
    { id: 'map', label: t('import.steps.map') },
    { id: 'preview', label: t('import.steps.preview') },
    { id: 'results', label: t('import.steps.results') },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size="xl"
        closeLabel={tCommon('action.close')}
        headerActions={
          importMutation.isPending ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 text-fg-tertiary hover:text-fg"
              aria-label={t('import.minimize')}
              onClick={() => handleOpenChange(false)}
            >
              <Minimize2 className="size-4" aria-hidden />
            </Button>
          ) : null
        }
      >
        <DialogHeader className={importMutation.isPending ? 'pr-20' : undefined}>
          <DialogTitle>{t('import.title')}</DialogTitle>
          <DialogDescription>{t('import.description')}</DialogDescription>
        </DialogHeader>

        <Stepper
          label={t('import.stepperLabel')}
          current={step}
          steps={steps}
          className="shrink-0"
        />

        <DialogBody>
          {step === 0 ? (
            <div className="space-y-4">
              <FileDropzone
                accept=".csv,text/csv"
                acceptLabel={t('import.upload.accept')}
                maxBytes={2 * 1024 * 1024}
                maxSizeLabel={t('import.upload.maxSize')}
                error={fileError ?? undefined}
                labels={{
                  prompt: t('import.upload.prompt'),
                  remove: tCommon('action.remove'),
                }}
                files={fileName ? [{ name: fileName }] : undefined}
                onRemove={() => {
                  setFileName(null);
                  setHeaders([]);
                  setRawRows([]);
                  setMapping({});
                  setFileError(null);
                }}
                onFiles={(files) => {
                  void onFiles(files);
                }}
              />
              <div className="flex justify-end">
                <Button type="button" variant="ghost" size="sm" onClick={downloadSample}>
                  {t('import.upload.downloadSample')}
                </Button>
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">{t('import.map.help')}</p>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[28rem] text-left text-body-sm">
                  <thead className="bg-surface-sunken text-fg-secondary">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('import.map.csvColumn')}</th>
                      <th className="px-3 py-2 font-medium">{t('import.map.trinityField')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {headers.map((header) => (
                      <tr key={header} className="border-t border-border">
                        <td className="px-3 py-2 align-middle font-medium text-fg">
                          {header || '—'}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Listbox
                            id={`map-${header}`}
                            value={mapping[header] ?? ''}
                            onChange={(value) => {
                              const nextField = (value ?? '') as ImportField | '';
                              setMapping((prev) => {
                                const next = { ...prev, [header]: nextField };
                                if (nextField) {
                                  for (const key of Object.keys(next)) {
                                    if (key !== header && next[key] === nextField) next[key] = '';
                                  }
                                }
                                return next;
                              });
                            }}
                            options={fieldOptions}
                            searchable
                            searchPlaceholder={t('import.map.searchFields')}
                            emptyMessage={t('import.map.noFieldMatch')}
                            popoverPortal="dialog"
                            clearable={false}
                            aria-label={t('import.map.trinityFieldForColumn', { column: header })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!mappedRequired ? (
                <p role="alert" className="text-caption text-danger-fg">
                  {t('import.map.requiredMissing')}
                </p>
              ) : null}
              <div className="max-w-xl space-y-3 rounded-lg border border-border bg-surface-sunken/40 p-4 pr-5">
                <div className="space-y-1">
                  <h3 className="text-body-sm font-semibold text-fg">
                    {t('members.form.appConnect.title')}
                  </h3>
                  <p className="text-caption text-fg-secondary">
                    {t('members.form.appConnect.description')}
                  </p>
                </div>
                <SwitchField
                  id="import-app-connect-email-same"
                  label={t('members.form.appConnect.emailSameAsContact')}
                  checked={appConnectEmailSameAsEmail}
                  onCheckedChange={setAppConnectEmailSameAsEmail}
                />
                <SwitchField
                  id="import-app-connect-phone-same"
                  label={t('members.form.appConnect.phoneSameAsContact')}
                  checked={appConnectPhoneSameAsPhone}
                  onCheckedChange={setAppConnectPhoneSameAsPhone}
                />
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-6">
              <p className="text-body-sm text-fg-secondary">
                {t('import.preview.summary', {
                  ready: readyRows.length,
                  problems: problemRows.length,
                  total: prepared.length,
                })}
              </p>

              {importMutation.isPending ? (
                <ProgressBar
                  label={t('import.progress.label')}
                  value={importProgress?.processed ?? 0}
                  max={importProgress?.total ?? importRowCount}
                  valueText={t('import.progress.value', {
                    processed: formatNumber(importProgress?.processed ?? 0),
                    total: formatNumber(importProgress?.total ?? importRowCount),
                  })}
                />
              ) : null}

              {problemRows.length > 0 ? (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <h3 className="text-body-sm font-semibold text-fg">
                      {t('import.preview.skippedTitle', { count: problemRows.length })}
                    </h3>
                    <p className="text-caption text-fg-secondary">
                      {t('import.preview.skippedDescription')}
                    </p>
                  </div>
                  <ScrollArea className="max-h-64 rounded-lg border border-border">
                    <table className="w-full min-w-[36rem] text-left text-body-sm">
                      <thead className="sticky top-0 z-10 bg-surface-sunken text-fg-secondary">
                        <tr>
                          <th className="px-3 py-2 font-medium">{t('import.preview.row')}</th>
                          <th className="px-3 py-2 font-medium">{t('members.columns.name')}</th>
                          <th className="px-3 py-2 font-medium">{t('import.preview.skippedReason')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {problemRows.map((row) => (
                          <tr key={row.index} className="border-t border-border">
                            <td className="px-3 py-2 tabular-nums text-fg-secondary">
                              {formatNumber(spreadsheetRowNumber(row.index))}
                            </td>
                            <td className="px-3 py-2 font-medium text-fg">
                              {displayName(row, mapping)}
                            </td>
                            <td className="px-3 py-2 text-caption text-danger-fg">
                              {row.issues.map(issueLabel).join(' · ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              ) : null}

              {readyRows.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-body-sm font-semibold text-fg">
                    {t('import.preview.readyTitle', { count: readyRows.length })}
                  </h3>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[36rem] text-left text-body-sm">
                      <thead className="bg-surface-sunken text-fg-secondary">
                        <tr>
                          <th className="px-3 py-2 font-medium">{t('import.preview.row')}</th>
                          <th className="px-3 py-2 font-medium">{t('members.columns.name')}</th>
                          <th className="px-3 py-2 font-medium">{t('members.columns.type')}</th>
                          <th className="px-3 py-2 font-medium">{t('members.columns.phone')}</th>
                          <th className="px-3 py-2 font-medium">{t('import.preview.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {readyRows.slice(0, 50).map((row) => (
                          <tr key={row.index} className="border-t border-border">
                            <td className="px-3 py-2 tabular-nums text-fg-secondary">
                              {formatNumber(spreadsheetRowNumber(row.index))}
                            </td>
                            <td className="px-3 py-2 font-medium text-fg">
                              {displayName(row, mapping)}
                            </td>
                            <td className="px-3 py-2 text-fg-secondary">
                              {row.payload.memberType
                                ? t(`members.type.${row.payload.memberType}`)
                                : '—'}
                            </td>
                            <td className="px-3 py-2 text-fg-secondary">
                              {row.payload.phone ?? '—'}
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant="success">{t('import.preview.ready')}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {readyRows.length > 50 ? (
                    <p className="text-caption text-fg-tertiary">
                      {t('import.preview.readyTruncated', {
                        shown: formatNumber(50),
                        total: formatNumber(readyRows.length),
                      })}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 3 && results ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('import.results.summary', {
                  succeeded: results.filter((row) => row.ok).length,
                  failed: results.filter((row) => !row.ok).length,
                })}
              </p>
              {results.some((row) => !row.ok) ? (
                <ul className="space-y-2">
                  {results
                    .filter((row) => !row.ok)
                    .slice(0, 40)
                    .map((row) => {
                      const preparedRow = readyRows[row.index];
                      const name = preparedRow
                        ? `${preparedRow.payload.firstName} ${preparedRow.payload.lastName}`
                        : t('import.results.rowLabel', { index: formatNumber(row.index + 1) });
                      return (
                        <li
                          key={row.index}
                          className="rounded-md border border-border px-3 py-2 text-body-sm"
                        >
                          <span className="font-medium text-fg">{name}</span>
                          <span className="mt-0.5 block text-caption text-danger-fg">
                            {row.error
                              ? tErrors(row.error, row.params)
                              : tErrors('MEMBER_IMPORT_ROW_FAILED')}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              ) : (
                <p className="text-body-sm text-fg">{t('import.results.allGood')}</p>
              )}
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          {step === 0 ? (
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
          ) : null}

          {step === 1 ? (
            <>
              <Button type="button" variant="outline" onClick={() => setStep(0)}>
                {tCommon('action.back')}
              </Button>
              <Button
                type="button"
                disabled={!mappedRequired}
                onClick={() => setStep(2)}
              >
                {tCommon('action.next')}
              </Button>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                disabled={importMutation.isPending}
              >
                {tCommon('action.back')}
              </Button>
              <Button
                type="button"
                loading={importMutation.isPending}
                disabled={
                  readyRows.length === 0 || importMutation.isPending
                }
                onClick={() => {
                  const importId = crypto.randomUUID();
                  setImportRowCount(readyRows.length);
                  setImportProgress({
                    processed: 0,
                    total: readyRows.length,
                    succeeded: 0,
                    failed: 0,
                  });
                  subscribeToImport(importId);
                  void importMutation.mutateAsync({
                    importId,
                    rows: readyRows.map((row) => row.payload),
                  });
                }}
              >
                {t('import.preview.confirm', { count: readyRows.length })}
              </Button>
            </>
          ) : null}

          {step === 3 ? (
            <div className="flex flex-wrap justify-end gap-2">
              {results?.some((row) => row.ok) ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const memberIds = results
                      .filter((row) => row.ok && row.memberId)
                      .map((row) => row.memberId!)
                      .join(',');
                    dismissSession();
                    onOpenChange(false);
                    void navigate(`/people/duplicates?run=1&memberIds=${memberIds}`);
                  }}
                >
                  {t('import.results.checkDuplicates')}
                </Button>
              ) : null}
              <Button type="button" onClick={() => handleOpenChange(false)}>
                {tCommon('action.close')}
              </Button>
            </div>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>

      {!open && backgroundActive && indicatorStatus ? (
        <MemberImportStatusIndicator
          status={indicatorStatus}
          label={indicatorLabel}
          openLabel={t('import.background.open')}
          dismissLabel={t('import.background.dismiss')}
          progress={
            indicatorStatus === 'importing' && importProgress
              ? { processed: importProgress.processed, total: importProgress.total }
              : undefined
          }
          progressLabel={t('import.progress.label')}
          progressValueText={
            importProgress
              ? t('import.progress.value', {
                  processed: formatNumber(importProgress.processed),
                  total: formatNumber(importProgress.total),
                })
              : undefined
          }
          onOpen={() => onOpenChange(true)}
          onDismiss={dismissSession}
        />
      ) : null}

      <ConfirmDialog
        open={importNavigationGuard.leaveOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) importNavigationGuard.stay();
        }}
        title={t('import.leaveGuard.title')}
        description={t('import.leaveGuard.description')}
        confirmLabel={t('import.leaveGuard.leave')}
        cancelLabel={t('import.leaveGuard.stay')}
        closeLabel={tCommon('action.close')}
        destructive
        onConfirm={importNavigationGuard.leave}
      />
    </>
  );
};
